import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/mysportset.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { RootPath } from "routes/types";
import { translate } from "../../i18n";
import { FEATURE_FLAGS } from "../../utils/feature-flags";
import { useAuth } from "./AuthProvider";
import { useSignUp } from "./hooks/useSignUp";
import RedirectAuthorizedUser from "./RedirectAuthorizedUser";
import { decodeJWT, isValidEmail, isValidPassword } from "./utils";

function SignUpPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { currentUser, setCurrentUser } = useAuth();
    const { mutateAsync: signUp, isLoading: isSigningUp, error: signUpError } = useSignUp((data) => {
        const decodedUser = decodeJWT(data.accessToken);
        setCurrentUser(decodedUser);
    });

    // Chakra color mode
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = useColorModeValue("gray.400", "gray.500");
    const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
    const textColorBrand = useColorModeValue("brand.500", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
    const googleText = useColorModeValue("navy.700", "white");
    const googleHover = useColorModeValue(
        { bg: "gray.200" },
        { bg: "whiteAlpha.300" }
    );
    const googleActive = useColorModeValue(
        { bg: "secondaryGray.300" },
        { bg: "whiteAlpha.200" }
    );
    const [show, setShow] = useState(false);
    const handleClick = () => setShow(!show);
    if(currentUser){
        return <RedirectAuthorizedUser user={currentUser}/>
    }

    return (
        <DefaultAuth illustrationBackground={illustration} image={illustration}>
            <Flex
                maxW={{ base: "100%", md: "max-content" }}
                w='100%'
                mx={{ base: "auto", lg: "0px" }}
                me='auto'
                h='100%'
                alignItems='start'
                justifyContent='center'
                mb={{ base: "30px", md: "60px" }}
                px={{ base: "25px", md: "0px" }}
                mt={{ base: "40px", md: "14vh" }}
                flexDirection='column'>
                <Box me='auto'>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        {translate('auth.signup')}
                    </Heading>
                    <Text
                        mb='36px'
                        ms='4px'
                        color={textColorSecondary}
                        fontWeight='400'
                        fontSize='md'>
                        {translate('auth.enter_info_to_sign_in')}
                    </Text>
                </Box>
                <Flex
                    zIndex='2'
                    direction='column'
                    w={{ base: "100%", md: "420px" }}
                    maxW='100%'
                    background='transparent'
                    borderRadius='15px'
                    mx={{ base: "auto", lg: "unset" }}
                    me='auto'
                    mb={{ base: "20px", md: "auto" }}>
                    {FEATURE_FLAGS.IS_GOOGLE_AUTH_ENBLAED && <Button
                        fontSize='sm'
                        me='0px'
                        mb='26px'
                        py='15px'
                        h='50px'
                        borderRadius='16px'
                        bg={googleBg}
                        color={googleText}
                        fontWeight='500'
                        _hover={googleHover}
                        _active={googleActive}
                        _focus={googleActive}>
                        <Icon as={FcGoogle} w='20px' h='20px' me='10px' />
                        {translate('auth.sign_in_with_google')}
                    </Button>}
                    {FEATURE_FLAGS.IS_GOOGLE_AUTH_ENBLAED && <Flex align='center' mb='25px'>
                        <HSeparator />
                        <Text color={textColorSecondary} mx='14px'>
                            {translate('auth.or')}
                        </Text>
                        <HSeparator />
                    </Flex>}
                    {signUpError && <Box mb='8px'>
                        <Alert fontSize='sm' status="error" variant="left-accent">
                            <AlertIcon />
                            <AlertDescription>
                                {translate('auth.sign_up_error_message')}
                            </AlertDescription>
                        </Alert>
                    </Box>}
                    <FormControl>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            mb='8px'>
                            {translate('auth.email')}<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isRequired={true}
                            variant='auth'
                            fontSize='sm'
                            ms={{ base: "0px", md: "0px" }}
                            type='email'
                            placeholder='mail@simmmple.com'
                            mb='24px'
                            fontWeight='500'
                            size='lg'
                        />
                        <FormLabel
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            display='flex'>
                            {translate('auth.password')}<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <InputGroup size='md'>
                            <Input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                isRequired={true}
                                fontSize='sm'
                                placeholder='********'
                                mb='24px'
                                size='lg'
                                type={show ? "text" : "password"}
                                variant='auth'
                            />
                            <InputRightElement display='flex' alignItems='center' mt='4px'>
                                <Icon
                                    color={textColorSecondary}
                                    _hover={{ cursor: "pointer" }}
                                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                    onClick={handleClick}
                                />
                            </InputRightElement>
                        </InputGroup>
                        <Button
                            isLoading={isSigningUp}
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='100%'
                            isDisabled={!isValidEmail(email) || !isValidPassword(password)}
                            h='50'
                            onClick={() => { signUp({email,password}) }}
                            mb='24px'>
                            {translate('auth.signup')}
                        </Button>
                    </FormControl>
                    <Flex
                        flexDirection='column'
                        justifyContent='center'
                        alignItems='start'
                        maxW='100%'
                        mt='0px'>
                        <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
                            <NavLink to={RootPath.AUTH}>
                                <Text
                                    color={textColorBrand}
                                    as='span'
                                    ms='5px'
                                    fontWeight='500'>
                                    {translate('auth.back_to_signin')}
                                </Text>
                            </NavLink>
                        </Text>
                    </Flex>
                </Flex>
            </Flex>
        </DefaultAuth>
    );
}

export default SignUpPage;
